/* GLOBAL POLE INVENTORY (2026-10-24) — the screen over backend/poleInventory.js: Max's monthly frame stock movement
   (Beginning → In → Out → Ending, quantity and KRW) per entity and frame family, closed around the 21st for the month
   before; the monthly frame sales beside it. Tabs: Summary (his summary sheet), Month (his month sheet, by family),
   Sales (his "montly sales q'ty"), Current position (YS: stock, sales and open POs in one view), Loads (ZCOR0120 and the NetSuite ledger uploaded, group 170 read from SAP, close),
   Mapping (what a load holds that maps to no family) and Direct-sale buyers (read only, Max). Project Approval's classes,
   like the Inventory Report. Admin only (`pole_inventory`). */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, FileUp, RefreshCw, Lock, Unlock, DatabaseZap } from 'lucide-react';
import Header from './Header';
import TabIcon from './TabIcon';
import ModuleHelp from './ModuleHelp';
import { BACKEND_URL } from './backendConfig';
import './SPHub.css';
import './ProjectApproval.css';
import './InventoryReport.css';
import './PoleInventory.css';

const API = `${BACKEND_URL}/api/pole-inventory`;
const ENTITIES = ['HINC', 'HUSA', 'HEMA', 'HPAC', 'HINC_POLE'];
const NS = ['HUSA', 'HEMA', 'HPAC'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monLabel = (ym) => (ym ? `${MON[Number(ym.slice(5)) - 1]}/${ym.slice(0, 4)}` : '');
const prevYm = (ym) => { const [y, m] = ym.split('-').map(Number); return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`; };
const range = (from, to) => { const out = []; let x = from; while (x <= to) { out.push(x); const [y, m] = x.split('-').map(Number); x = m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`; } return out; };
const n0 = (v) => (v === null || v === undefined || v === '' ? '' : Math.round(Number(v)).toLocaleString('en-US'));
const mil = (v) => (v === null || v === undefined ? '—' : (Number(v) / 1e6).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }));
const fmtWhen = (v) => { if (!v) return ''; const d = new Date(v); if (Number.isNaN(d.getTime())) return String(v); const vn = new Date(d.getTime() + 7 * 3600 * 1000); return `${vn.getUTCDate()}/${MON[vn.getUTCMonth()]}/${vn.getUTCFullYear()} ${vn.toISOString().slice(11, 16)}`; };
const call = async (method, path, body) => {
  const r = await fetch(`${API}${path}`, { method, credentials: 'include', headers: body ? { 'Content-Type': 'application/json' } : undefined, body: body ? JSON.stringify(body) : undefined });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `HTTP ${r.status}`);
  return json;
};
const sum = (rows, k) => rows.reduce((a, r) => a + (Number(r[k]) || 0), 0);
/* the month before last ended is the one being closed (Max closes around the 21st) */
const lastClosable = () => { const d = new Date(); const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; return prevYm(ym); };

/* ---------------------------------------------------------------- summary */
function Summary({ t, data }) {
  const LINES = [['begin', 'lBegin'], ['in', 'lIn'], ['out', 'lOut'], ['end', 'lEnd']];
  const of = (m, e) => data.rows.filter((r) => r.ym === m && r.entity === e);
  const last = data.months[data.months.length - 1];
  const endQ = (m) => ENTITIES.reduce((a, e) => a + sum(of(m, e), 'end_qty'), 0);
  const endV = (m) => ENTITIES.reduce((a, e) => a + sum(of(m, e), 'end_krw'), 0);
  const prev = data.months.length > 1 ? data.months[data.months.length - 2] : null;
  return (
    <div className="pa-section">
      <div className="iv-tiles">
        <div className="iv-tile"><span>{t('xPole.tEndQty', { m: monLabel(last) })}</span><b>{n0(endQ(last))}</b><em>{prev ? t('xPole.vsPrev', { d: `${endQ(last) - endQ(prev) >= 0 ? '+' : '−'}${n0(Math.abs(endQ(last) - endQ(prev)))}` }) : t('xPole.pcs')}</em></div>
        <div className="iv-tile"><span>{t('xPole.tEndValue', { m: monLabel(last) })}</span><b>₩{mil(endV(last))}M</b><em>{t('xPole.krwMil')}</em></div>
        <div className="iv-tile"><span>{t('xPole.tFinished')}</span><b>{n0(['HINC', 'HUSA', 'HEMA', 'HPAC'].reduce((a, e) => a + sum(of(last, e), 'end_qty'), 0))}</b><em>{t('xPole.tFinishedSub')}</em></div>
        <div className="iv-tile"><span>{t('xPole.tPole')}</span><b>{n0(sum(of(last, 'HINC_POLE'), 'end_qty'))}</b><em>{t('xPole.tPoleSub')}</em></div>
      </div>
      <div className="iv-wrap pi-freeze">
        <table className="pa-table pi-sum">
          <thead>
            <tr><th rowSpan={2} className="pi-stick">{t('xPole.entity')}</th><th rowSpan={2} className="pi-stick2">{t('xPole.line')}</th>
              {data.months.map((m) => <th key={m} colSpan={2}>{monLabel(m)}{data.status[m] === 'closed' && <Lock size={11} className="pi-lock" aria-label={t('xPole.closed')} />}</th>)}</tr>
            <tr>{data.months.map((m) => <React.Fragment key={m}><th className="num">{t('xPole.qty')}</th><th className="num">{t('xPole.krwM')}</th></React.Fragment>)}</tr>
          </thead>
          <tbody>
            {ENTITIES.map((e) => (
              <React.Fragment key={e}>
                {LINES.map(([k, lab], i) => (
                  <tr key={k} className={i === 0 ? 'pi-first' : ''}>
                    <td className="pi-stick">{i === 0 && <b>{t(`xPole.e_${e}`)}</b>}</td>
                    <td className="pi-stick2">{t(`xPole.${lab}${NS.includes(e) ? 'Ns' : ''}`)}</td>
                    {data.months.map((m) => { const l = of(m, e); return <React.Fragment key={m}><td className="num">{l.length ? n0(sum(l, `${k}_qty`)) : ''}</td><td className="num">{l.length ? mil(sum(l, `${k}_krw`)) : ''}</td></React.Fragment>; })}
                  </tr>
                ))}
                <tr className="pi-gap">
                  <td className="pi-stick" />
                  <td className="pi-stick2" title={t(NS.includes(e) ? 'xPole.fxHint' : 'xPole.gapHint')}>{t(NS.includes(e) ? 'xPole.lFx' : 'xPole.lGap')}</td>
                  {data.months.map((m) => {
                    const l = of(m, e);
                    const v = NS.includes(e) ? sum(l, 'fx_krw') : sum(l, 'end_krw') - (sum(l, 'begin_krw') + sum(l, 'in_krw') - sum(l, 'out_krw'));
                    return <React.Fragment key={m}><td className="num" /><td className={`num ${v < 0 ? 'iv-neg' : ''}`}>{l.length ? mil(v) : ''}</td></React.Fragment>;
                  })}
                </tr>
              </React.Fragment>
            ))}
            <tr className="pi-fx"><td className="pi-stick" /><td className="pi-stick2">{t('xPole.lRate')}</td>
              {data.months.map((m) => <React.Fragment key={m}><td className="num" title={data.fx[m]?.endDate || ''}>{data.fx[m]?.end ? data.fx[m].end.toFixed(2) : '—'}</td><td className="num">{data.fx[m]?.avg ? data.fx[m].avg.toFixed(2) : ''}</td></React.Fragment>)}</tr>
          </tbody>
        </table>
      </div>
      <p className="iv-hint pi-foot">{t('xPole.sumFoot')}</p>
    </div>
  );
}

/* ---------------------------------------------------------------- one month by family */
function MonthView({ t, data, month, setMonth }) {
  const [stage, setStage] = useState('end');
  const rows = data.rows.filter((r) => r.ym === month);
  const idx = useMemo(() => new Map(rows.map((r) => [`${r.entity}|${r.family_code}`, r])), [rows]);
  const fams = data.families.filter((f) => ENTITIES.some((e) => idx.get(`${e}|${f.family_code}`)));
  return (
    <div className="pa-section">
      <div className="pa-toolbar iv-filters">
        <label className="iv-field"><span>{t('xPole.month')}</span>
          <select value={month} onChange={(e) => setMonth(e.target.value)}>{[...data.months].reverse().map((m) => <option key={m} value={m}>{monLabel(m)}{data.status[m] === 'closed' ? ` · ${t('xPole.closed')}` : ''}</option>)}</select>
        </label>
        <div className="pa-tabs pi-stages">{['begin', 'in', 'out', 'end'].map((s) => <button key={s} type="button" className={`pa-tab${stage === s ? ' active' : ''}`} onClick={() => setStage(s)}>{t(`xPole.s_${s}`)}</button>)}</div>
      </div>
      <div className="iv-wrap pi-freeze">
        <table className="pa-table">
          <thead><tr><th>{t('xPole.code')}</th><th>{t('xPole.family')}</th>{ENTITIES.map((e) => <th key={e} className="num">{t(`xPole.e_${e}`)}</th>)}<th className="num">{t('xPole.total')}</th></tr></thead>
          <tbody>
            {fams.map((f) => {
              const v = ENTITIES.map((e) => Number(idx.get(`${e}|${f.family_code}`)?.[`${stage}_qty`] || 0));
              return <tr key={f.family_code}><td className="iv-code">{f.family_code}</td><td>{f.family_name}</td>{v.map((x, i) => <td key={i} className="num">{n0(x)}</td>)}<td className="num"><b>{n0(v.reduce((a, x) => a + x, 0))}</b></td></tr>;
            })}
            <tr className="iv-total"><td /><td>{t('xPole.totalQty')}</td>{ENTITIES.map((e) => <td key={e} className="num">{n0(sum(rows.filter((r) => r.entity === e), `${stage}_qty`))}</td>)}<td className="num">{n0(sum(rows, `${stage}_qty`))}</td></tr>
            <tr className="iv-total"><td /><td>{t('xPole.totalKrw')}</td>{ENTITIES.map((e) => <td key={e} className="num">{mil(sum(rows.filter((r) => r.entity === e), `${stage}_krw`))}</td>)}<td className="num">{mil(sum(rows, `${stage}_krw`))}</td></tr>
          </tbody>
        </table>
      </div>
      {!fams.length && <p className="iv-empty">{t('xPole.nothingMonth')}</p>}
    </div>
  );
}

/* ---------------------------------------------------------------- sales */
/* Max's two sales sheets: sheet 1 by entity, sheet 2 with each entity's direct-sale (DT) buyers split out — a sale goes to
   "<entity> DT" when the entity that made it sold to a buyer on the DT list (Max, 24/10). Months up to `typedThrough`
   keep what his file typed (tinted); where the portal reads it differently its figure is printed small under it. */
function Sales({ t, flash }) {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [sheet, setSheet] = useState(1);
  const [data, setData] = useState(null);
  useEffect(() => { let live = true; setData(null); call('GET', `/sales?year=${year}`).then((d) => { if (live) setData(d); }).catch((e) => flash(e.message, true)); return () => { live = false; }; }, [year, flash]);
  const years = []; for (let y = new Date().getFullYear(); y >= 2019; y -= 1) years.push(String(y));
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const rows = data?.sheets?.find((x) => x.sheet === sheet)?.rows || [];
  const isTyped = (r, i) => r.typed[i] !== null && data.typedThrough && `${year}-${String(i + 1).padStart(2, '0')}` <= data.typedThrough;
  return (
    <div className="pa-section">
      <div className="pa-toolbar iv-filters">
        <label className="iv-field"><span>{t('xPole.year')}</span><select value={year} onChange={(e) => setYear(e.target.value)}>{years.map((y) => <option key={y} value={y}>{y}</option>)}</select></label>
        <div className="pa-tabs pi-stages">{[1, 2].map((n) => <button key={n} type="button" className={`pa-tab${sheet === n ? ' active' : ''}`} onClick={() => setSheet(n)}>{t(`xPole.sheet${n}`)}</button>)}</div>
        {data && <span className="iv-hint pi-inline">{t('xPole.salesThrough', { d: data.through })}{data.typedThrough ? ` · ${t('xPole.typedThrough', { m: monLabel(data.typedThrough) })}` : ''}</span>}
      </div>
      {!data ? <p className="iv-hint">{t('xPole.loading')}</p> : (
        <div className="iv-wrap">
          <table className="pa-table pi-sales">
            <thead><tr><th>{t('xPole.product')}</th>{months.map((m) => <th key={m} className="num">{m}</th>)}<th className="num">{t('xPole.total')}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.label} className={/ DT$/.test(r.label) ? 'pi-dtrow' : ''}><td><b>{r.label}</b></td>
                  {r.used.map((v, i) => {
                    const ty = isTyped(r, i); const diff = ty && Math.round(r.typed[i]) !== Math.round(r.computed[i]);
                    return <td key={i} className={`num${ty ? ' pi-typedcell' : ''}${diff ? ' pi-diff' : ''}`} title={diff ? t('xPole.portalSays', { n: n0(r.computed[i]) }) : ''}>{v ? n0(v) : ''}{diff && <span className="pi-typed">{n0(r.computed[i])}</span>}</td>;
                  })}
                  <td className="num"><b>{n0(r.used.reduce((a, x) => a + x, 0))}</b></td></tr>
              ))}
              <tr className="iv-total"><td>{t('xPole.total')}</td>{months.map((m) => <td key={m} className="num">{n0(rows.reduce((a, r) => a + r.used[m - 1], 0)) || ''}</td>)}<td className="num">{n0(rows.reduce((a, r) => a + r.used.reduce((b, x) => b + x, 0), 0))}</td></tr>
            </tbody>
          </table>
        </div>
      )}
      <p className="iv-hint pi-foot">{t(sheet === 2 ? 'xPole.salesFoot2' : 'xPole.salesFoot')}</p>
    </div>
  );
}

/* ---------------------------------------------------------------- current position (YS, 22/10)
   The month last loaded, then what came in and went out since, today's stock and what is still on order — one row per
   entity × family, the entity's total under its rows. Received = into the entity's own stock (Max: not in transit); HINC's
   shipments on to HUSA / HEMA / HPAC are their own column (netted into received, Chair Zero read −3,144);
   "Other movements" is what received and sold do not explain, shown rather than hidden. Read live (~20 s), kept 30 min. */
const MOS_WARN = 2;
function Position({ t, flash }) {
  const [data, setData] = useState(null);
  const [entity, setEntity] = useState('');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(() => new Set());
  const load = useCallback((refresh) => {
    let live = true; setBusy(true); if (refresh) setData(null);
    call('GET', `/position${refresh ? '?refresh=1' : ''}`).then((d) => { if (live) setData(d); })
      .catch((e) => flash(e.message, true)).finally(() => { if (live) setBusy(false); });
    return () => { live = false; };
  }, [flash]);
  useEffect(() => load(false), [load]);
  if (!data) return <div className="pa-section"><p className="iv-hint">{busy ? t('xPole.posReading') : t('xPole.loading')}</p></div>;
  const ents = Object.keys(data.totals).filter((e) => !entity || e === entity);
  const ml = (ym) => monLabel(ym);
  const mos = (v) => (v === null || v === undefined ? '—' : v.toLocaleString('en-US', { maximumFractionDigits: 1, minimumFractionDigits: 1 }));
  const signed = (v) => { const r = Math.round(v); return r > 0 ? `+${n0(r)}` : n0(r); };
  const z = (v) => (Math.round(v) ? n0(v) : '');
  const toggle = (e) => setOpen((s) => { const n = new Set(s); if (n.has(e)) n.delete(e); else n.add(e); return n; });
  const cells = (x) => (<>
    <td className="num">{n0(x.end_qty)}</td>
    <td className="num">{z(x.received)}</td>
    <td className="num">{z(x.to_group)}</td>
    <td className="num">{z(x.sold)}</td>
    <td className={`num pi-other${Math.round(x.other) ? '' : ' pi-zero'}`}>{signed(x.other)}</td>
    <td className="num pi-now"><b>{n0(x.now)}</b></td>
    <td className={`num${x.po.overdue ? ' pi-overdue' : ''}`}>{z(x.po.overdue)}</td>
    {data.months.map((m) => <td key={m} className="num">{z(x.po[m])}</td>)}
    <td className="num">{z(x.po.later)}</td>
    <td className="num">{z(x.po.nodate)}</td>
    <td className="num"><b>{z(x.po_total)}</b></td>
    <td className="num">{n0(x.avg3)}</td>
    <td className={`num${x.mos !== null && x.mos < MOS_WARN ? ' pi-low' : ''}`}>{mos(x.mos)}</td>
    <td className="num">{mos(x.mos_po)}</td>
  </>);
  return (
    <div className="pa-section">
      <div className="pa-toolbar iv-filters">
        <label className="iv-field"><span>{t('xPole.entity')}</span>
          <select value={entity} onChange={(e) => setEntity(e.target.value)}><option value="">{t('xPole.allEntities')}</option>{Object.keys(data.totals).map((e) => <option key={e} value={e}>{e}</option>)}</select></label>
        <button type="button" className="pa-btn small" onClick={() => setOpen(new Set(ents))}>{t('xPole.expandAll')}</button>
        <button type="button" className="pa-btn small" onClick={() => setOpen(new Set())}>{t('xPole.collapseAll')}</button>
        <span className="iv-hint pi-inline">{t('xPole.posWindow', { base: ml(data.base), from: data.from, to: data.to, s: data.salesThrough })}</span>
        <span className="iv-grow" />
        <button type="button" className="pa-btn small" disabled={busy} title={t('xPole.posRefresh')} onClick={() => load(true)}><RefreshCw size={13} /> {busy ? t('xPole.running') : t('xPole.posRefresh')}</button>
        <a className="pa-btn small primary" href={`${API}/position.xlsx?base=${data.base}`}><Download size={13} /> {t('xPole.export')}</a>
      </div>
      <div className="iv-wrap pi-freeze">
        <table className="pa-table pi-pos">
          <thead>
            <tr>
              <th rowSpan={2} className="pi-stick">{t('xPole.product')}</th>
              <th colSpan={6} className="pi-grp">{t('xPole.posStock')}</th>
              <th colSpan={data.months.length + 4} className="pi-grp">{t('xPole.posOpenPo')}</th>
              <th colSpan={3} className="pi-grp">{t('xPole.posCover')}</th>
            </tr>
            <tr>
              <th className="num">{t('xPole.posEnd', { m: ml(data.base) })}</th>
              <th className="num" title={t('xPole.posReceivedHint')}>{t('xPole.posReceived')}</th>
              <th className="num" title={t('xPole.posToGroupHint')}>{t('xPole.posToGroup')}</th>
              <th className="num">{t('xPole.posSold')}</th>
              <th className="num" title={t('xPole.posOtherHint')}>{t('xPole.posOther')}</th>
              <th className="num">{t('xPole.posNow')}</th>
              <th className="num">{t('xPole.posOverdue')}</th>
              {data.months.map((m) => <th key={m} className="num">{ml(m)}</th>)}
              <th className="num">{t('xPole.posLater')}</th>
              <th className="num">{t('xPole.posNoDate')}</th>
              <th className="num">{t('xPole.total')}</th>
              <th className="num" title={t('xPole.posAvgHint', { m: ml(data.base) })}>{t('xPole.posAvg')}</th>
              <th className="num" title={t('xPole.posMosHint')}>{t('xPole.posMos')}</th>
              <th className="num">{t('xPole.posMosPo')}</th>
            </tr>
          </thead>
          <tbody>
            {ents.map((e) => {
              const rows = data.rows.filter((x) => x.entity === e); const isOpen = open.has(e);
              return (
                <React.Fragment key={e}>
                  <tr className="pi-ent" data-entity={e} onClick={() => toggle(e)}>
                    <td className="pi-stick"><button type="button" className="pi-fold" aria-expanded={isOpen} onClick={(ev) => { ev.stopPropagation(); toggle(e); }}>{isOpen ? '▾' : '▸'}</button> <b>{e}</b> <span className="iv-hint">{t('xPole.posFamilies', { n: rows.length })}</span></td>
                    {cells(data.totals[e])}
                  </tr>
                  {isOpen && rows.map((x) => <tr key={`${e}|${x.family_code}`} className="pi-famrow"><td className="pi-stick">{x.family_name}</td>{cells(x)}</tr>)}
                </React.Fragment>
              );
            })}
            {!entity && <tr className="iv-total pi-grouprow"><td className="pi-stick">{t('xPole.posGroup')}</td>{cells(data.group)}</tr>}
          </tbody>
        </table>
      </div>
      <p className="iv-hint pi-foot">{t('xPole.posFoot', { n: n0(data.hincForSubs) })}</p>
      {data.unmappedFrames?.length > 0 && <p className="iv-hint pi-foot">{t('xPole.posUnmapped', { list: data.unmappedFrames.slice(0, 8).map((u) => `${u.frame_model} (${n0(u.qty)})`).join(', ') })}</p>}
    </div>
  );
}

/* ---------------------------------------------------------------- loads */
const SOURCES = [['sap', 'srcSap'], ['ns', 'srcNs'], ['internal', 'srcInternal']];
function Loads({ t, state, edit, reload, flash, onChanged }) {
  const [busy, setBusy] = useState('');
  const [extra, setExtra] = useState('');
  const loaded = useMemo(() => {
    const m = new Map();
    for (const l of state.loads) { const k = l.ym; if (!m.has(k)) m.set(k, {}); const o = m.get(k); if (l.is_current && l.status === 'ok') o[l.source] = l; else if (!o[l.source] && l.status !== 'ok') o[`${l.source}_last`] = l; }
    return m;
  }, [state.loads]);
  const status = Object.fromEntries(state.months.map((x) => [x.ym, x]));
  const months = [...new Set([lastClosable(), ...(extra ? [extra] : []), ...loaded.keys(), ...state.months.map((x) => x.ym)])].sort().reverse();
  const run = async (key, fn, done) => { setBusy(key); try { const r = await fn(); flash(done(r)); await reload(); onChanged(); } catch (e) { flash(e.message, true); } finally { setBusy(''); } };
  const upload = (ym, source) => {
    const input = document.createElement('input'); input.type = 'file'; input.accept = '.xlsx,.xls';
    input.onchange = () => {
      const f = input.files?.[0]; if (!f) return;
      const rd = new FileReader();
      rd.onload = () => run(`${ym}|${source}`, () => call('POST', `/month/${ym}/upload/${source}`, { fileName: f.name, data: String(rd.result).split(',')[1] }),
        (r) => `${monLabel(ym)} · ${t(`xPole.${SOURCES.find((s) => s[0] === source)[1]}`)}: ${t('xPole.rowsRead', { n: n0(r.rows) })}${r.summary?.warning ? ` — ${r.summary.warning}` : ''}`);
      rd.readAsDataURL(f);
    };
    input.click();
  };
  return (
    <div className="pa-section">
      <p className="iv-hint">{t('xPole.loadsHint')}</p>
      {edit && <div className="pa-toolbar iv-filters"><label className="iv-field"><span>{t('xPole.otherMonth')}</span><input type="month" value={extra} onChange={(e) => setExtra(e.target.value)} /></label></div>}
      <div className="iv-wrap pi-freeze">
        <table className="pa-table pi-loads">
          <thead><tr><th>{t('xPole.month')}</th>{SOURCES.map(([s, lab]) => <th key={s}>{t(`xPole.${lab}`)}</th>)}<th>{t('xPole.status')}</th><th className="cw-actions">{t('xPole.action')}</th></tr></thead>
          <tbody>
            {months.map((ym) => {
              const o = loaded.get(ym) || {}; const st = status[ym]; const closed = st?.status === 'closed';
              return (
                <tr key={ym}>
                  <td className="iv-code"><b>{monLabel(ym)}</b></td>
                  {SOURCES.map(([s]) => {
                    const l = o[s]; const bad = o[`${s}_last`];
                    return (
                      <td key={s} className="pi-src">
                        {l ? <><span className="pi-ok">✓ {n0(l.row_count)} {t('xPole.rows')}</span><span className="iv-sub" title={l.file_name || ''}>{l.started_by} · {fmtWhen(l.started_at)}</span>{l.summary?.warning && <span className="pi-warn">{l.summary.warning}</span>}</> : <span className="iv-sub">{t('xPole.notLoaded')}</span>}
                        {bad && bad.status === 'error' && <span className="pi-warn" title={bad.error_text}>{t('xPole.lastFailed')}: {String(bad.error_text || '').slice(0, 80)}</span>}
                        {edit && !closed && (
                          <span className="pi-btns">
                            {s !== 'internal' && <button type="button" className="pa-btn small" disabled={!!busy} onClick={() => upload(ym, s)}><FileUp size={12} /> {busy === `${ym}|${s}` ? t('xPole.running') : t('xPole.upload')}</button>}
                            {s === 'internal' && <button type="button" className="pa-btn small" disabled={!!busy} onClick={() => run(`${ym}|internal`, () => call('POST', `/month/${ym}/pull/internal`), (r) => `${monLabel(ym)}: ${t('xPole.rowsRead', { n: n0(r.rows) })}`)}><DatabaseZap size={12} /> {busy === `${ym}|internal` ? t('xPole.running') : t('xPole.readSap')}</button>}
                          </span>
                        )}
                      </td>
                    );
                  })}
                  <td>{closed ? <span className="pi-closed"><Lock size={12} /> {t('xPole.closedBy', { by: st.closed_by, when: fmtWhen(st.closed_at) })}</span> : <span className="iv-sub">{t('xPole.open')}</span>}</td>
                  <td className="cw-actions">{edit && (closed
                    ? <button type="button" className="pa-btn small" disabled={!!busy} onClick={() => run(`${ym}|reopen`, () => call('POST', `/month/${ym}/reopen`), () => t('xPole.reopened', { m: monLabel(ym) }))}><Unlock size={12} /> {t('xPole.reopen')}</button>
                    : <button type="button" className="pa-btn small primary" disabled={!!busy || !(o.sap || o.ns)} title={!(o.sap || o.ns) ? t('xPole.closeNeeds') : ''} onClick={() => run(`${ym}|close`, () => call('POST', `/month/${ym}/close`), (r) => t('xPole.closedOk', { m: monLabel(ym), n: n0(r.rows) }))}><Lock size={12} /> {t('xPole.close')}</button>)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- mapping */
/* Max, 24/10: a list of what looks like a frame, ready to accept. The server suggests a family (the same name in the KS
   map, a mapped name the item's name holds, or a family's own name); the box opens on it, "Only likely frames" hides the
   rest, and one press saves every suggestion shown — after asking in place. */
function Mapping({ t, months, edit, flash, onChanged }) {
  const [ym, setYm] = useState(months[months.length - 1] || '');
  const [data, setData] = useState(null);
  const [pick, setPick] = useState({});
  const [likely, setLikely] = useState(true);
  const [asking, setAsking] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { setData(null); setPick({}); call('GET', `/maps?ym=${ym}`).then(setData).catch((e) => flash(e.message, true)); }, [ym, flash]);
  useEffect(() => { load(); }, [load]);
  const famOf = (kind, r) => { const k = `${kind}|${kind === 'sap' ? r.material : r.parent_name}`; return k in pick ? pick[k] : (r.suggest?.family || ''); };
  const save = async (kind, key, fam) => {
    try { await call('PUT', `/maps/${kind}`, { key, family: fam || null }); flash(t('xPole.mapSaved', { key })); load(); onChanged(); } catch (e) { flash(e.message, true); }
  };
  const famName = (code) => data?.families.find((f) => f.family_code === code)?.family_name || code;
  const why = (s) => (s ? t(`xPole.why_${s.why}`, { term: s.term }) : '');
  const famSelect = (kind, r) => {
    const key = kind === 'sap' ? r.material : r.parent_name; const fam = famOf(kind, r);
    return (
      <span className="pi-btns">
        <select value={fam} className={r.suggest && fam === r.suggest.family ? 'pi-suggested' : ''} onChange={(e) => setPick((p) => ({ ...p, [`${kind}|${key}`]: e.target.value }))}>
          <option value="">{t('xPole.notFrame')}</option>
          {(data?.families || []).filter((f) => f.is_active).map((f) => <option key={f.family_code} value={f.family_code}>{f.family_code} · {f.family_name}</option>)}
        </select>
        <button type="button" className="pa-btn small" onClick={() => save(kind, key, fam)}>{t('xPole.save')}</button>
      </span>
    );
  };
  const shownOf = (kind) => (data?.unmapped[kind] || []).filter((r) => !likely || r.suggest);
  const acceptable = (kind) => shownOf(kind).filter((r) => r.suggest && famOf(kind, r));
  const accept = async (kind) => {
    const items = acceptable(kind).map((r) => ({ key: kind === 'sap' ? r.material : r.parent_name, family: famOf(kind, r) }));
    setBusy(true);
    try { const r = await call('POST', `/maps/${kind}/bulk`, { items }); flash(t('xPole.acceptedN', { n: r.saved })); setAsking(''); load(); onChanged(); } catch (e) { flash(e.message, true); } finally { setBusy(false); }
  };
  const acceptBar = (kind) => {
    const n = acceptable(kind).length;
    if (!edit || !n) return null;
    return asking === kind
      ? <span className="pi-btns pi-ask">{t('xPole.acceptAsk', { n })} <button type="button" className="pa-btn small primary" disabled={busy} onClick={() => accept(kind)}>{t('xPole.acceptYes')}</button><button type="button" className="pa-btn small" onClick={() => setAsking('')}>{t('xPole.cancel')}</button></span>
      : <button type="button" className="pa-btn small primary" onClick={() => setAsking(kind)}>{t('xPole.acceptN', { n })}</button>;
  };
  const sugCell = (r) => (r.suggest ? <span className="pi-sug" title={why(r.suggest)}>{famName(r.suggest.family)}<em>{why(r.suggest)}</em></span> : <span className="iv-sub">—</span>);
  return (
    <div className="pa-section">
      <div className="pa-toolbar iv-filters">
        <label className="iv-field"><span>{t('xPole.month')}</span><select value={ym} onChange={(e) => setYm(e.target.value)}>{[...months].reverse().map((x) => <option key={x} value={x}>{monLabel(x)}</option>)}</select></label>
        <label className="pi-check"><input type="checkbox" checked={likely} onChange={(e) => { setLikely(e.target.checked); setAsking(''); }} /> {t('xPole.onlyLikely')}</label>
        {data && <span className="iv-hint pi-inline">{t('xPole.mapCounts', { fam: data.families.length, sap: n0(data.sap.length), ns: n0(data.ns.length) })}</span>}
      </div>
      <p className="iv-hint">{t('xPole.mapHint')}</p>
      {!data ? <p className="iv-hint">{t('xPole.loading')}</p> : (
        <div className="pi-two">
          <div>
            <div className="pi-headrow"><h3 className="iv-h">{t(likely ? 'xPole.unSapLikely' : 'xPole.unSap', { n: shownOf('sap').length, total: data.unmapped.sap.length })}</h3>{acceptBar('sap')}</div>
            <div className="iv-wrap pi-freeze pi-short"><table className="pa-table">
              <thead><tr><th>{t('xPole.material')}</th><th>{t('xPole.description')}</th><th>{t('xPole.type')}</th><th className="num">{t('xPole.endQty')}</th><th>{t('xPole.suggested')}</th>{edit && <th>{t('xPole.family')}</th>}</tr></thead>
              <tbody>{shownOf('sap').map((r) => <tr key={r.material}><td className="iv-code">{r.material}</td><td>{r.mat_desc}</td><td>{r.mat_type}</td><td className="num">{n0(r.end_qty)}</td><td>{sugCell(r)}</td>{edit && <td>{famSelect('sap', r)}</td>}</tr>)}
                {!shownOf('sap').length && <tr><td colSpan={6} className="iv-empty">{t(likely ? 'xPole.noneLikely' : 'xPole.allMapped')}</td></tr>}</tbody>
            </table></div>
          </div>
          <div>
            <div className="pi-headrow"><h3 className="iv-h">{t(likely ? 'xPole.unNsLikely' : 'xPole.unNs', { n: shownOf('ns').length, total: data.unmapped.ns.length })}</h3>{acceptBar('ns')}</div>
            <div className="iv-wrap pi-freeze pi-short"><table className="pa-table">
              <thead><tr><th>{t('xPole.parent')}</th><th className="num">{t('xPole.lines')}</th><th className="num">{t('xPole.endQty')}</th><th>{t('xPole.suggested')}</th>{edit && <th>{t('xPole.family')}</th>}</tr></thead>
              <tbody>{shownOf('ns').map((r) => <tr key={r.parent_name}><td>{r.parent_name}</td><td className="num">{n0(r.lines)}</td><td className="num">{n0(r.end_qty)}</td><td>{sugCell(r)}</td>{edit && <td>{famSelect('ns', r)}</td>}</tr>)}
                {!shownOf('ns').length && <tr><td colSpan={5} className="iv-empty">{t(likely ? 'xPole.noneLikely' : 'xPole.allMapped')}</td></tr>}</tbody>
            </table></div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- direct-sale buyers (read only) */
/* the list, and under it which sales customers the DT split matched to each buyer this year — so a wrong match
   (a store whose name happens to start like a buyer's) can be seen */
function Buyers({ t, flash }) {
  const [rows, setRows] = useState(null);
  const [matches, setMatches] = useState(null);
  const [q, setQ] = useState('');
  const year = new Date().getFullYear();
  useEffect(() => {
    call('GET', '/dt-buyers').then((d) => setRows(d.buyers)).catch((e) => flash(e.message, true));
    call('GET', `/sales?year=${year}`).then((d) => setMatches(d.matches || [])).catch(() => setMatches([]));
  }, [flash, year]);
  const shown = (rows || []).filter((r) => !q || Object.values(r).join(' ').toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="pa-section">
      <p className="iv-hint">{t('xPole.buyersHint')}</p>
      <div className="pa-toolbar iv-filters"><input className="pi-search" placeholder={t('xPole.search')} value={q} onChange={(e) => setQ(e.target.value)} /><span className="iv-sub">{t('xPole.nOf', { n: shown.length, total: (rows || []).length })}</span></div>
      <div className="iv-wrap pi-freeze pi-buyers"><table className="pa-table">
        <thead><tr><th>{t('xPole.buyer')}</th><th>{t('xPole.cBefore')}</th><th>{t('xPole.c2020')}</th><th>{t('xPole.c2026')}</th><th>{t('xPole.country')}</th><th>{t('xPole.pricing')}</th></tr></thead>
        <tbody>{shown.map((r) => <tr key={r.buyer}><td>{r.buyer}</td><td>{r.class_before_2020}</td><td>{r.class_2020_2025}</td><td>{r.class_from_2026}</td><td>{r.country}</td><td>{r.pricing}</td></tr>)}</tbody>
      </table></div>
      <h3 className="iv-h">{t('xPole.matchesTitle', { y: year })}</h3>
      <p className="iv-hint">{t('xPole.matchesHint')}</p>
      <div className="iv-wrap pi-matches"><table className="pa-table">
        <thead><tr><th>{t('xPole.buyer')}</th><th>{t('xPole.entity')}</th><th>{t('xPole.salesCustomer')}</th><th className="num">{t('xPole.qty')}</th></tr></thead>
        <tbody>{(matches || []).map((m) => <tr key={`${m.buyer}|${m.entity}|${m.customer}`}><td>{m.buyer}</td><td>{m.entity}</td><td>{m.customer}</td><td className="num">{n0(m.qty)}</td></tr>)}
          {matches && !matches.length && <tr><td colSpan={4} className="iv-empty">{t('xPole.noMatches')}</td></tr>}</tbody>
      </table></div>
    </div>
  );
}

/* THE GUIDE (Max 01/11: every module has one). It walks the Summary tab, the one the screen opens on; the tour switches
   back to it first. The tiles and the table are skipped by TourGuide while the figures are still loading. */
const TOUR = [
  { selector: '.pi-page .iv-weekbar', title: 'tourPole.rangeT', body: 'tourPole.rangeB' },
  { selector: '.pi-page .view-tabs', title: 'tourPole.tabsT', body: 'tourPole.tabsB' },
  { selector: '.pi-page .iv-tiles', title: 'tourPole.tilesT', body: 'tourPole.tilesB' },
  { selector: '.pi-page .pi-sum', title: 'tourPole.tableT', body: 'tourPole.tableB' },
];

export default function PoleInventory() {
  const { t } = useTranslation();
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'summary');
  const [state, setState] = useState(null);
  const [to, setTo] = useState(lastClosable());
  /* twelve months up to the one being closed */
  const [from, setFrom] = useState(() => { let x = lastClosable(); for (let i = 0; i < 11; i += 1) x = prevYm(x); return x; });
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(lastClosable());
  const [stamp, setStamp] = useState(0);
  const [msg, setMsg] = useState(null);
  const flash = useCallback((text, bad) => setMsg({ text, bad }), []);
  const reload = useCallback(async () => { try { setState(await call('GET', '/months')); } catch (e) { flash(e.message, true); } }, [flash]);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    if (!from || !to || to < from) return undefined;
    let live = true; setData(null);
    call('GET', `/figures?from=${from}&to=${to}`).then((d) => { if (live) setData(d); }).catch((e) => flash(e.message, true));
    return () => { live = false; };
  }, [from, to, stamp, flash]);
  const pickTab = (k) => { setTab(k); const u = new URL(window.location.href); u.searchParams.set('tab', k); window.history.replaceState(null, '', u); };
  const tabs = [['summary', t('xPole.tab_summary')], ['month', t('xPole.tab_month')], ['sales', t('xPole.tab_sales')], ['position', t('xPole.tab_position')], ['loads', t('xPole.tab_loads')], ['map', t('xPole.tab_map')], ['buyers', t('xPole.tab_buyers')]];
  const edit = !!state?.me?.edit;
  const months = data?.months || range(from, to);
  return (
    <div className="sp-page">
      <Header />
      <div className="sp-main iv-page pi-page">
        <div className="mh-titlerow">
          <div>
            <h2 className="pa-title">{t('xPole.title')}</h2>
            <p className="iv-hint">{t('xPole.sub')}</p>
          </div>
          <ModuleHelp steps={TOUR} tourKey="poleInventory" ready={!!data} onBeforeTour={() => pickTab('summary')} />
        </div>
        {msg && <div className={`iv-flash${msg.bad ? ' bad' : ''}`} role="status">{msg.text} <button type="button" className="pa-btn small" onClick={() => setMsg(null)}>×</button></div>}
        <div className="pa-toolbar iv-weekbar">
          <label className="iv-field"><span>{t('xPole.from')}</span><input type="month" value={from} max={to} onChange={(e) => setFrom(e.target.value)} /></label>
          <label className="iv-field"><span>{t('xPole.to')}</span><input type="month" value={to} min={from} onChange={(e) => setTo(e.target.value)} /></label>
          <span className="iv-grow" />
          <button type="button" className="pa-btn small" title={t('xPole.refresh')} onClick={() => { reload(); setStamp((s) => s + 1); }}><RefreshCw size={13} /></button>
          <a className="pa-btn small primary" href={`${API}/export?from=${from}&to=${to}`}><Download size={13} /> {t('xPole.export')}</a>
        </div>
        <div className="pa-tabs view-tabs" role="tablist">
          {tabs.map(([k, label]) => <button key={k} type="button" role="tab" aria-selected={tab === k} className={`pa-tab${tab === k ? ' active' : ''}`} onClick={() => pickTab(k)}><TabIcon k={k} />{label}</button>)}
        </div>
        {tab === 'summary' && (data ? <Summary t={t} data={data} /> : <p className="iv-hint">{t('xPole.loading')}</p>)}
        {tab === 'month' && (data ? <MonthView t={t} data={data} month={data.months.includes(month) ? month : data.months[data.months.length - 1]} setMonth={setMonth} /> : <p className="iv-hint">{t('xPole.loading')}</p>)}
        {tab === 'sales' && <Sales t={t} flash={flash} />}
        {tab === 'position' && <Position t={t} flash={flash} />}
        {tab === 'loads' && (state ? <Loads t={t} state={state} edit={edit} reload={reload} flash={flash} onChanged={() => setStamp((s) => s + 1)} /> : <p className="iv-hint">{t('xPole.loading')}</p>)}
        {tab === 'map' && <Mapping t={t} months={months} edit={edit} flash={flash} onChanged={() => setStamp((s) => s + 1)} />}
        {tab === 'buyers' && <Buyers t={t} flash={flash} />}
      </div>
    </div>
  );
}
